import DragAndDropList from 'core/dragAndDropList';
import {
  addSearchQuery,
  changeSearchGroupCombinator,
  copySearchGroup,
  deleteSearchGroup,
} from 'modules/queryBuilder/legacyQueryBuilder/actions';
import {
  EMPTY_SEARCH_QUERY,
  SEARCH_GROUP_ID_PREFIX,
} from 'modules/queryBuilder/legacyQueryBuilder/helpers';
import {
  selectEmptySearchQuery,
  selectIsViewMode,
  selectSearchGroupCombinator,
  selectSearchQueries,
} from 'modules/queryBuilder/legacyQueryBuilder/selectors';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import noop from 'utils/noop';
import ActionButtons from '../actionButtons';
import CombinatorDropdown from '../combinatorDropdown';
import SearchQueryGroup from '../searchQueryGroup';
import SegmentConnector from '../segmentConnector';
import {
  ActionsContainer,
  Container,
  QueryContainer,
  SearchGroupContainer,
} from './SearchGroup.styled';
import { ElementIndexContextProvider, ElementType } from '../../contexts';

// type SearchQueryProps = {
//   /** Property value of the search query */
//   dataType: string,
//   /** The search combo object of the search query. */
//   searchCombo: {},
// };

// type Props = {
//   /** The animation time in milliseconds. */
//   animationTime: number,
//   /** The combinator of the base search query. */
//   combinator: string,
//   /** Array of properties to hide. */
//   hiddenProperties: Array<string>,
//   /** Array of properties to disable. */
//   disabledProperties: Array<string>,
//   /** The value of an empty search query. */
//   emptySearchQuery: {},
//   /**
//    * Callback function that returns the selected property to get fields for if fields do not exist.
//    *
//    * ```
//    * getFieldsForProperty(property: string) => {}
//    * ```
//    * @param { String } property: The property to get fields for.
//    * @returns { void }
//    */
//   getFieldsForProperty: (property: string) => {},
//   /** The combinator of this search group. */
//   groupCombinator: string,
//   /** Append text of group combinator. */
//   groupCombinatorAppendText: string,
//   /** Prepend text of group combinator. */
//   groupCombinatorPrependText: string,
//   /** Hides grouping if only one search query group exists. */
//   hideGroupsOnSingle: boolean,
//   /** If query builder is in view mode. */
//   isViewMode: boolean,
//   /** If query builder is in read only mode. */
//   isReadOnly?: boolean,
//   /** Disables animations. */
//   noAnimation: boolean,
//   /**
//    * Callback when add search group button is clicked.
//    *
//    * ```
//    * onAddSearchGroup() => {}
//    * ```
//    * @returns { void }
//    */
//   onAddSearchGroup: () => {},
//   /**
//    * Callback function when adding a search query.
//    *
//    * ```
//    * onAddSearchQuery(searchQuery, searchGroupIndex) => {}
//    * ```
//    * @param { Object } searchQuery: The search query to add.
//    * @param { Number } searchGroupIndex: The index of the search group to add the search query.
//    * @returns { void }
//    */
//   onAddSearchQuery: (searchQuery: {}, searchGroupIndex: number) => {},
//   /**
//    * Callback function when changing the combinator.
//    *
//    * ```
//    * onChangeCombinator(combinator, searchGroupIndex) => {}
//    * ```
//    * @param { String } combinator: The updated combinator value.
//    * @param { Number } searchGroupIndex: The index of the search group to change the combinator of.
//    * @returns { void }
//    */
//   onChangeCombinator: (combinator: string, searchGroupIndex: number) => {},
//   /**
//    * Callback function when copying a search group.
//    *
//    * ```
//    * onCopySearchGroup(searchGroupIndex) => {}
//    * ```
//    * @param { Number } searchGroupIndex: The index of the search group to copy.
//    * @returns { void }
//    */
//   onCopySearchGroup: (searchGroupIndex: number) => {},
//   /**
//    * Callback function when deleting a search group.
//    *
//    * ```
//    * onDeleteSearchGroup(searchGroupIndex) => {}
//    * ```
//    * @param { Number } searchGroupIndex: The index of the search group to delete.
//    * @returns { void }
//    */
//   onDeleteSearchGroup: () => {},
//   /** The index of the search group. */
//   searchGroupIndex: number,
//   /** The list of search queries for this search group. */
//   searchQueries: [SearchQueryProps],
//   /** Store object to get state. (Used for connected SearchQueryGroup component) */
//   store: {},
//   /** The total number of search groups. */
//   totalGroups: number,
//   /** Whether or not to display a cast type field for specific Long type criteria property fields */
//   useCastTypeField: boolean,
//   /** Array of properties to restrict to if specified. See usage in src/modules/queryBuilder/legacyQueryBuilder/components/propertyDropdown/index.js */
//   restrictToProperties?: Array<string>,
// };

class SearchGroup extends React.Component {
  static defaultProps = {
    animationTime: 200,
    combinator: 'or',
    emptySearchQuery: EMPTY_SEARCH_QUERY,
    hideGroupsOnSingle: false,
    isReadOnly: false,
    isViewMode: false,
    noAnimation: false,
    onAddSearchGroup: noop,
    onAddSearchQuery: noop,
    onChangeCombinator: noop,
    searchQueries: [],
  };

  constructor(props) {
    super(props);
    const { noAnimation, animationTime } = props;

    this.state = {
      animationTime: noAnimation ? 0 : animationTime,
      isDeleting: false,
    };
  }

  /**
   * hasMultipleGroups
   * Function to determine if there is more than one search group.
   * @returns { Boolean } If there are multiple search groups.
   */
  hasMultipleGroups = () => {
    const { hideGroupsOnSingle, isViewMode, totalGroups } = this.props;
    const numberOfGroupsToShow = hideGroupsOnSingle ? 1 : 0;
    return totalGroups > numberOfGroupsToShow || isViewMode;
  };

  /**
   * handleAddSearchQuery
   * Callabck function when adding a search query.
   * @returns { void }
   */
  handleAddSearchQuery = () => {
    const { emptySearchQuery, searchGroupIndex, onAddSearchQuery } = this.props;
    onAddSearchQuery(emptySearchQuery, searchGroupIndex);
  };

  /**
   * handleChangeCombinator
   * Callabck function when changing the combinator of the search group.
   * @param { Object } e: The change event.
   * @param { Object } props: The updated props of the combinator dropdown containing the updated value.
   * @returns { void }
   */
  handleChangeCombinator = (e, { value }) => {
    const { searchGroupIndex, onChangeCombinator } = this.props;
    onChangeCombinator(value, searchGroupIndex);
  };

  /**
   * handleCopySearchGroup
   * Callabck function when copying the search group.
   * @returns { void }
   */
  handleCopySearchGroup = () => {
    const { searchGroupIndex, onCopySearchGroup } = this.props;
    onCopySearchGroup(searchGroupIndex);
  };

  /**
   * postDelete
   * Callback function after delete action has been called. Calls handleDeleteSearchGroup.
   * @returns { void }
   */
  postDelete = () => {
    const { searchGroupIndex, onDeleteSearchGroup } = this.props;
    this.setState({ isDeleting: false });
    onDeleteSearchGroup(searchGroupIndex);
  };

  /**
   * handleDelete
   * Callback function when delete button is clicked. Sets isDeleting state flag for delete animation.
   * @returns { void }
   */
  handleDeleteSearchGroup = () => {
    const { animationTime } = this.state;
    this.setState({ isDeleting: true });
    setTimeout(this.postDelete, animationTime);
  };

  /**
   * renderSearchQueries
   * Geberates the search query groups.
   * @returns { Array } The search queries.
   */
  renderSearchQueries = () => {
    const {
      combinator,
      hiddenPropertySections,
      hiddenProperties,
      disabledProperties,
      getFieldsForProperty,
      searchGroupIndex,
      searchQueries,
      store,
      useCastTypeField,
      isReadOnly,
      filteredFieldsByPropertyMap,
      restrictToProperties,
      properties,
      showFieldTextInput,
      type,
    } = this.props;
    const totalQueries = searchQueries.length;

    return searchQueries.map((searchQuery, index) => (
      <ElementIndexContextProvider
        key={`search-query-${index}`}
        type={ElementType.Requirement}
        index={index}
      >
        <SearchQueryGroup
          type={type}
          hiddenPropertySections={hiddenPropertySections}
          hiddenProperties={hiddenProperties}
          disabledProperties={disabledProperties}
          getFieldsForProperty={getFieldsForProperty}
          hasMultipleGroups={this.hasMultipleGroups()}
          queryCombinator={combinator}
          searchGroupIndex={searchGroupIndex}
          searchQueryIndex={index}
          store={store}
          totalQueries={totalQueries}
          useCastTypeField={useCastTypeField}
          isReadOnly={isReadOnly}
          filteredFieldsByPropertyMap={filteredFieldsByPropertyMap}
          restrictToProperties={restrictToProperties}
          properties={properties}
          showFieldTextInput={showFieldTextInput}
          canSetMaxPropertyCount={this.props.canSetMaxPropertyCount}
        />
      </ElementIndexContextProvider>
    ));
  };

  render() {
    const {
      combinator,
      groupCombinator,
      groupCombinatorAppendText,
      groupCombinatorPrependText,
      hideGroupsOnSingle,
      isReadOnly,
      isViewMode,
      onAddSearchGroup,
      searchGroupIndex,
      totalGroups,
    } = this.props;
    const multipleGroups = this.hasMultipleGroups();
    const isLastGroup = searchGroupIndex === totalGroups - 1;
    return (
      <SearchGroupContainer isViewMode={isViewMode}>
        {multipleGroups && (
          <SegmentConnector
            isMainConnector
            showLabel={isViewMode}
            isLastConnector={isLastGroup}
            value={groupCombinator}
            isReadOnly={isReadOnly}
          />
        )}
        {multipleGroups && !isViewMode && (
          <SegmentConnector
            isGroupConnector
            isLastConnector={searchGroupIndex === totalGroups - 1}
            isTopConnector={searchGroupIndex === 0}
            value={groupCombinator}
            isReadOnly={isReadOnly}
          />
        )}
        <Container data-role="search-group-container">
          {!isViewMode && (
            <ActionsContainer>
              <CombinatorDropdown
                appendText={groupCombinatorAppendText}
                className="cssCombinatorDropdown"
                onChange={this.handleChangeCombinator}
                prependText={groupCombinatorPrependText}
                value={combinator}
                isReadOnly={isReadOnly}
              >
                {!isReadOnly && (
                  <ActionButtons
                    canDelete={totalGroups > 1}
                    className="cssActionContainerButtons"
                    copyText="Copy Group"
                    deleteText="Delete Group"
                    isGroupActionButtons
                    onCopy={this.handleCopySearchGroup}
                    onDelete={this.handleDeleteSearchGroup}
                    showBorders
                    trashIconDelete
                    copyIconOutline
                  />
                )}
              </CombinatorDropdown>
            </ActionsContainer>
          )}
          <QueryContainer isViewMode={isViewMode}>
            <DragAndDropList.Droppable
              className="cssDroppableContainer"
              draggingOverClass="cssDraggingOver"
              id={`${SEARCH_GROUP_ID_PREFIX}${searchGroupIndex}`}
              disabled={isReadOnly}
            >
              {this.renderSearchQueries()}
            </DragAndDropList.Droppable>
            {!isViewMode && (
              <SegmentConnector
                isAddButton
                onClick={this.handleAddSearchQuery}
                onClickAddGroup={onAddSearchGroup}
                showAddGroupButton={hideGroupsOnSingle && totalGroups < 2}
                value={combinator}
                isReadOnly={isReadOnly}
              />
            )}
          </QueryContainer>
        </Container>
      </SearchGroupContainer>
    );
  }
}

export { SearchGroup as SearchGroupComponent };

const mapStateToProps = (state, props) => ({
  combinator: selectSearchGroupCombinator(state, props),
  emptySearchQuery: selectEmptySearchQuery(state, props),
  isViewMode: selectIsViewMode(state),
  searchQueries: selectSearchQueries(state, props),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      onAddSearchQuery: addSearchQuery,
      onChangeCombinator: changeSearchGroupCombinator,
      onCopySearchGroup: copySearchGroup,
      onDeleteSearchGroup: deleteSearchGroup,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(SearchGroup);
